/**
 * A searchable single-choice dropdown: type to filter, arrow keys, Enter.
 * react-select styled with the same tokens as `.nl-select`, so it sits next
 * to the plain inputs without looking different, and follows dark mode.
 *
 * The menu is portalled to <body> (see portalMenu), so a table row or a modal
 * with overflow hidden cannot clip it.
 *
 * With `onCreate`, typing a name that matches nothing offers
 * "{createLabel} “name”" as the last row; choosing it calls onCreate with the
 * typed text and leaves the value to the caller.
 */
import React, {useMemo} from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import {portalMenu} from './index';

export interface SearchSelectOption {
  value: string;
  label: string;
}

interface Props {
  options: SearchSelectOption[];
  /** The chosen value; '' or null for none. */
  value: string | null | undefined;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
  /** Amber border, e.g. for a required choice not yet made. */
  warn?: boolean;
  'aria-label'?: string;
  /** Offer to create what was typed. */
  onCreate?: (text: string) => void;
  /** E.g. "Add new product" — shown before the typed text. */
  createLabel?: string;
}

export const searchSelectStyles = (warn = false) => ({
  control: (base: any, state: any) => ({
    ...base,
    minHeight: '40px',
    borderRadius: '10px',
    backgroundColor: state.isDisabled ? 'var(--surface2)' : 'var(--surface)',
    borderColor: warn ? '#fbbf24' : state.isFocused ? 'var(--brand)' : 'var(--border2)',
    boxShadow: state.isFocused ? '0 0 0 3px var(--brandSoft)' : 'none',
    '&:hover': {borderColor: state.isFocused ? 'var(--brand)' : 'var(--border2)'},
    fontSize: '14px',
  }),
  valueContainer: (base: any) => ({...base, padding: '0 10px'}),
  singleValue: (base: any, state: any) => ({
    ...base,
    color: state.isDisabled ? 'var(--text3)' : 'var(--text)',
  }),
  input: (base: any) => ({...base, color: 'var(--text)'}),
  placeholder: (base: any) => ({...base, color: 'var(--text3)'}),
  indicatorSeparator: () => ({display: 'none'}),
  menu: (base: any) => ({
    ...base,
    backgroundColor: 'var(--surface)',
    border: '1px solid var(--border)',
    borderRadius: '10px',
    overflow: 'hidden',
    fontSize: '14px',
  }),
  option: (base: any, state: any) => ({
    ...base,
    cursor: 'pointer',
    color: state.isSelected ? 'var(--brand)' : 'var(--text)',
    fontWeight: state.isSelected ? 500 : 400,
    backgroundColor: state.isFocused ? 'var(--surface2)' : 'transparent',
    ':active': {backgroundColor: 'var(--surface2)'},
  }),
  noOptionsMessage: (base: any) => ({...base, color: 'var(--text3)'}),
});

const SearchSelect = ({
  options,
  value,
  onChange,
  placeholder,
  disabled,
  warn,
  onCreate,
  createLabel = 'Add',
  ...rest
}: Props) => {
  const selected = useMemo(
    () => options.find((o) => o.value === value) ?? null,
    [options, value],
  );
  const common = {
    'aria-label': rest['aria-label'],
    options,
    value: selected,
    onChange: (opt: any) => onChange(opt?.value ?? ''),
    placeholder,
    isDisabled: disabled,
    isSearchable: true,
    ...portalMenu(searchSelectStyles(warn)),
  };

  if (onCreate) {
    return (
      <CreatableSelect
        {...common}
        onCreateOption={(text: string) => onCreate(text.trim())}
        formatCreateLabel={(text: string) => `${createLabel} “${text.trim()}”`}
        noOptionsMessage={() => 'Type a name to search or add'}
      />
    );
  }
  return (
    <Select
      {...common}
      noOptionsMessage={({inputValue}) => (inputValue ? `No match for “${inputValue}”` : 'Nothing to choose')}
    />
  );
};

export default SearchSelect;
